import React from "react";

import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@client/shadcn/ui/sheet";
import { Button } from "@client/shadcn/ui/button";
import { interfaces, ProjectsTemplates_Project_T } from "@reactivated";
import { Editor } from "@client/shadcn/lexical/blocks/editor-00/editor";
import { SerializedEditorState } from "lexical";
import { AspectRatio } from "@client/shadcn/ui/aspect-ratio";
import { Label } from "@client/shadcn/ui/label";
import { Badge } from "@client/shadcn/ui/badge";
import { Progress } from "@client/shadcn/ui/progress";

interface Props {
    className?: string;
    id: number;
}

export const ProjectDetail = (props: Props) => {
    const [project, setProject] = React.useState<ProjectsTemplates_Project_T>();
    
    const onOpen = async (event: React.MouseEvent) => {
        const response = await fetch(`/projects/${props.id}/`, {
            headers: {
                Accept: "application/json",
            },
        });
        const data: interfaces.ProjectEdit = await response.json();
        setProject(data.project);
    };

    return (
        <Sheet>
            <SheetTrigger asChild>
                <div className="absolute cursor-pointer size-full" onClick={onOpen} />
            </SheetTrigger>
            
            <SheetContent className="overflow-y-scroll [&>button:first-of-type]:hidden">
                <SheetHeader className="p-0">
                    <AspectRatio ratio={1 / 1} className="h-full w-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300">
                        {project?.splash_art.url ?
                            <img src={project.splash_art.url} alt={project.title}/> :
                            <Label className="text-gray-500 text-4xl font-medium">{project?.title.charAt(0)}</Label>
                        }
                        <div className="absolute top-2 right-2">
                            <Badge>{project?.status}</Badge>
                        </div>
                    </AspectRatio>
                </SheetHeader>

                <SheetHeader>
                    <SheetTitle className="text-xl">{project?.title}</SheetTitle>
                    <SheetDescription className="text-base">
                        {project?.details ?
                            <Editor
                                editorSerializedState={JSON.parse(project.details)}
                                readOnly={true}
                            />
                        : null}
                    </SheetDescription>
                </SheetHeader>

                <SheetFooter className="flex gap-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between text-xs">
                            <Label>Progress</Label>
                            <Label>{project?.progress}%</Label>
                        </div>
                        <Progress value={project?.progress} className="h-3" />
                    </div>
                    <div className="flex justify-start flex-wrap gap-2 basis-1/4">
                        {(project?.tags || []).map((tag) => (
                            <Badge key={tag.name} variant="outline" className="text-sm">{tag.name}</Badge>
                        )) || <Badge className="invisible"/>}
                    </div>
                    <div className="flex justify-between">
                        {
                            project?.contact ? (
                                <div className="flex flex-col gap-1">
                                    <Label>{`${project.contact.first_name} ${project.contact.last_name}`}</Label>
                                    <Label asChild className="font-light text-stone-400"><a href={`mailto:${project.contact.email}`}>{project.contact.email}</a></Label>
                                </div>
                            ) : <div />
                        }
                        <Label className="font-light text-stone-400">{
                            project?.start_date ? (() => {
                                const startDate = new Date(project.start_date);
                                const today = new Date();
                                if (startDate < today) {
                                    return `Started on ${startDate.toLocaleDateString()}`;
                                } else {
                                    return `Starting on ${startDate.toLocaleDateString()}`;
                                }
                            })() : null
                        }</Label>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
